import { useEffect, useState } from "react";

import { Button, Divider, FormControl, FormErrorMessage, FormLabel, Input, useDisclosure, useToast } from "@chakra-ui/react"
import { Field, FieldProps, Form, Formik } from "formik"
import { useLocation } from "react-router-dom"
import { HttpStatusCode } from "axios";
import { format } from "date-fns";
import DatePicker from "react-datepicker";

import ArrowIcon from "@/assets/ArrowIcon";
import { COLORS, CURRENCY, DURATION_TOAST, FORMAT_ADD_DATE, PAYMENT_STATUS, SIRE_REVIEW_INVOICE_TYPE } from "@/common/constants";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import FileUploader from "@/components/Fields/FileUploader";
import Loader from "@/components/Layouts/Loader";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import { formatCurrency } from "@/utils/currency";
import CurrencyInput from "@/components/Fields/CurrencyInput";
import { invoiceSireReviewPaymentSchema } from "@/schemas/invoiceSireReviewPaymentSchema";
import { getSireReviewReviewerInvoiceDetails, postUpdateSireReviewReviewerInvoicePayment } from "@/services/InvoiceSireReviewReviewerService";
import { getSireReviewRequestorInvoiceDetails, postUpdateSireReviewRequestorInvoicePayment } from "@/services/InvoiceSireReviewRequestorService";

import '../styles/Form.css'

const SireReviewPayment = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const location = useLocation();
    const name = useAppSelector((state: RootState) => state.account.name);

    const { id, type } = location.state || {};

    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmationTitleButton, setConfirmationTitleButton] = useState("");
    const [dataDetail, setDataDetail] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [paymentFile, setPaymentFile] = useState<any>(null);
    const [title, setTitle] = useState("");
    const [dataEdit, setDataEdit] = useState<any>(null);
    const [amountIDR, setAmountIDR] = useState<number>(0);

    const handleCancel = () => {
        onOpen();
        setTitle("Cancel Payment?");
        setConfirmationTest("Are you sure you want to cancel?");
        setConfirmationTitleButton("Yes");
    }

    const handleSubmit = (data: any) => {
        setTitle("Confirm Payment?");
        setConfirmationTest("Are you sure you want to confirm?");
        setConfirmationTitleButton("Confirm Payment");
        onOpen();
        setDataEdit(data);
        setIsSubmitting(true);
    }

    const generateDataPayment = () => {
        const data = new FormData();
        data.append('id', id);
        data.append('paymentStatus', String(PAYMENT_STATUS.PAID));
        data.append('paymentDate', dataEdit ? format(dataEdit.paymentDate, FORMAT_ADD_DATE) : '');
        data.append('modifiedBy', name || 'System');
        data.append('file', paymentFile);
        if (type === SIRE_REVIEW_INVOICE_TYPE.INSPECTOR){
            data.append('totalAmountIDR', String(amountIDR));
        }
        if (type === SIRE_REVIEW_INVOICE_TYPE.REQUESTOR){
            data.append('requestorId', dataDetail.requestorId);
        }
        // for (let [key, value] of data.entries()) {
        //     console.log(`${key}: ${value}`);
        // }
        return data;
    }

    const handleConfirm = async () => {
        onClose();

        if (title === "Cancel Payment?") {
            history.back();
            return;
        }

        if (type === SIRE_REVIEW_INVOICE_TYPE.INSPECTOR && amountIDR <= 0) {
            toast({
                title: 'Total amount in IDR is required',
                description: '',
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            return;
        }

        const obj = generateDataPayment();
        let response = {} as any;
        if (type === SIRE_REVIEW_INVOICE_TYPE.REQUESTOR) {
            response = await postUpdateSireReviewRequestorInvoicePayment(obj);
        } else if (type === SIRE_REVIEW_INVOICE_TYPE.INSPECTOR) {
            response = await postUpdateSireReviewReviewerInvoicePayment(obj);
        }

        setIsSubmitting(false);
        if (response?.status === HttpStatusCode.Ok) {
            toast({
                title: 'Payment confirmed.',
                description: "We've confirmed your payment.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });

            setTimeout(() => {
                history.back();
            }, 1000)
        } else {
            toast({
                title: 'Payment failed to confirmed.',
                description: "We could not confirm your payment.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const handleDrop = (acceptedFiles: File[], setFieldValue: any) => {
        setFieldValue('paymentFile', acceptedFiles[0]);
        setPaymentFile(acceptedFiles[0]);
    };

    const getDataDetailRequestor = async () => {
        const response = await getSireReviewRequestorInvoiceDetails(id);
        if (response) {
            setDataDetail(response.data);
        }
        setIsLoading(false);
    }

    const getDataDetailInspector = async () => {
        const response = await getSireReviewReviewerInvoiceDetails(id);
        if (response) {
            setDataDetail(response.data);
        }
        setIsLoading(false);
    }

    const handleChangeAmountIDR = (amount: number, props: {setFieldValue: (fieldName: string, newValue: number) => void}) => {
        props.setFieldValue('amountIDR', amount);
        setAmountIDR(amount);
    }

    useEffect(() => {
        //console.log(id, type)
        if (id && type === SIRE_REVIEW_INVOICE_TYPE.REQUESTOR) {
            getDataDetailRequestor();
        } else if (id && type === SIRE_REVIEW_INVOICE_TYPE.INSPECTOR) {
            getDataDetailInspector();
        }
    }, [id])

    return (
        <>
            {isLoading ? <Loader /> :
                <>
                    <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmationTitleButton} />
                    <div className="form-container">
                        <Formik
                            initialValues={{
                                reviewer: dataDetail?.inspectionCompanyName ?? '',
                                invoiceID: dataDetail?.invoiceID ?? dataDetail?.invoiceNumber,
                                invoiceDate: dataDetail ? format(dataDetail?.invoiceDate, 'dd-MM-yyyy') : '',
                                paymentDate: format(new Date(), FORMAT_ADD_DATE).toString() || '',
                                totalReviews: dataDetail?.totalReview ?? '',
                                amount: formatCurrency(dataDetail?.totalAmount, dataDetail?.currency, dataDetail?.currency === CURRENCY.IDR.code ? 'id-ID' : 'en-US'),
                                paymentFile: null
                            }}
                            validationSchema={() => invoiceSireReviewPaymentSchema(type)}
                            onSubmit={(values) => {
                                handleSubmit(values);
                            }}
                            enableReinitialize
                        >
                            {(props) => (
                                <Form>
                                    {(type === SIRE_REVIEW_INVOICE_TYPE.INSPECTOR) && <Field name='reviewer'>
                                        {({ field }: FieldProps) => (
                                            <FormControl style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Reviewer Name</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} disabled placeholder='Reviewer Name' className="form-input" />
                                                    </div>
                                                    
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>}
                                    <Field name='invoiceID'>
                                        {({ field }: FieldProps) => (
                                            <FormControl style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Invoice ID</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} disabled placeholder='Invoice ID' className="form-input" />
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='invoiceDate'>
                                        {({ field }: FieldProps) => (
                                            <FormControl style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Invoice Date</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} disabled placeholder='Invoice Date' className="form-input" />
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='paymentDate'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.paymentDate && form.touched.paymentDate)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Payment Date<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div style={{
                                                        width: '100%',
                                                        height: '2.5rem',
                                                        fontSize: '1rem',
                                                        paddingInlineStart: '1rem',
                                                        paddingInlineEnd: '1rem',
                                                        borderRadius: '0.375rem',
                                                        minWidth: '0px',
                                                        outline: '2px solid transparent',
                                                        outlineOffset: '2px',
                                                        position: 'relative',
                                                        appearance: 'none',
                                                        transitionProperty: 'background-color, border-color, color, fill, stroke, opacity, box-shadow, transform',
                                                        paddingTop: '0.35rem',
                                                        marginBottom: (form.errors.paymentDate && form.touched.paymentDate) ? '1.25rem' : 0,
                                                        border: form.errors.paymentDate && form.touched.paymentDate ? '2px solid red' : '1px solid #e5e7e9'
                                                    }}>
                                                        <DatePicker
                                                            id="paymentDate"
                                                            selected={field.value as Date | null}
                                                            onChange={(date: Date | null) => props.setFieldValue('paymentDate', date)}
                                                            dateFormat="dd-MM-YYYY"
                                                            placeholderText="Payment Date"
                                                            readOnly={isSubmitting}
                                                        />
                                                        <FormErrorMessage style={{ marginLeft: '-1rem', marginTop: '12px' }}>{String(form.errors.paymentDate)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    {(type === SIRE_REVIEW_INVOICE_TYPE.INSPECTOR) && <Field name='totalReviews'>
                                        {({ field }: FieldProps) => (
                                            <FormControl style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Total Reviews</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} disabled placeholder='Total Reviews' className="form-input" />
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>}
                                    {(type === SIRE_REVIEW_INVOICE_TYPE.REQUESTOR && dataDetail?.isMonthly) && <Field name='totalReviews'>
                                        {({ field }: FieldProps) => (
                                            <FormControl style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Review Month</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} disabled placeholder='Review Month' className="form-input" />
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>}
                                    {/*(type === SIRE_REVIEW_INVOICE_TYPE.REQUESTOR && (dataDetail?.isMonthly === false)) && <Field name='totalReviews'>
                                        {({ field }: FieldProps) => (
                                            <FormControl style={{ marginBottom: 15 }}>
                                                <div style={{ display: 'flex' }}>
                                                    <FormLabel className="form-label">Order Number</FormLabel>
                                                    <Input {...field} disabled placeholder='Order Number' className="form-input" />
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>*/}
                                    <Field name='amount'>
                                        {({ field }: FieldProps) => (
                                            <FormControl style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Total Amount</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} disabled placeholder='Total Amount' className="form-input" />
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    {(type === SIRE_REVIEW_INVOICE_TYPE.INSPECTOR) && <Field name='amountIDR'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.amountIDR && form.touched.amountIDR)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Total Amount in IDR<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <CurrencyInput
                                                            {...field}
                                                            placeholder='Total Amount in IDR'
																prefix={CURRENCY.IDR.code}
                                                            value={amountIDR}
                                                            error={Boolean(form.errors.amountIDR && form.touched.amountIDR)}
                                                            onValueChange={({ floatValue = 0 }) => handleChangeAmountIDR(floatValue, props)}
                                                            disabled={isSubmitting}
                                                        />
                                                        <FormErrorMessage>{String(form.errors.amountIDR)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>}
                                    <Field name='paymentFile'>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.paymentFile && (form.touched.paymentFile || form.submitCount))} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Payment File<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <FileUploader onDrop={(e) => handleDrop(e, props.setFieldValue)} isInvalid={Boolean(form.errors.paymentFile && (form.touched.paymentFile || form.submitCount))} isDisabled={props.isSubmitting} type='supporting-files' />
                                                        <FormErrorMessage>{String(form.errors.paymentFile)}</FormErrorMessage>
                                                    </div>
                                                    
                                                </div>
                                                
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Divider />
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            mt={4}
                                            bgColor='white'
                                            borderColor='#D0D5DD'
                                            isLoading={isSubmitting}
                                            marginRight={5}
                                            onClick={handleCancel}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            mt={4}
                                            bgColor={COLORS.BUTTON.BACKGROUND}
                                            color={COLORS.BUTTON.FOREGROUND}
                                            isLoading={isSubmitting}
                                            type='submit'
                                            rightIcon={<ArrowIcon />}
                                        >
                                            Confirm Payment
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </>}
        </>
    )
}

export default SireReviewPayment;
